import {
	ChangeEvent,
	KeyboardEvent,
	MouseEvent,
	ReactElement,
	Ref,
	useCallback,
	useContext,
	useMemo
} from "react";
import classNames from "classnames";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import {
	CalendarStylesColor,
	FormattedTextString
} from "client/Misc/commonTypes";
import {
	isNotNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import {
	IconValue,
	Icon,
	isSvgIcon,
	unsetIconColor
} from "../Icon/Icon";
import { Spinner } from "../Spinner/Spinner";
import styles from "./Checkbox.module.less";

export type CheckboxColor =
	| "info"
	| "success"
	| "warning"
	| "danger"
	| CalendarStylesColor
	| "primary";

export type CheckboxProps = {
	value: boolean;
	onChange(value: boolean): void;
	trackingId?: string;
	isDisabled?: boolean;
	text?: string | FormattedTextString;
	title?: string;
	ellipseText?: boolean;
	isCentered?: boolean;
	icon?: IconValue;
	color?: CheckboxColor;
	hasBusyIndicator?: boolean;
	shouldBubbleClick?: boolean;
	tabIndex?: number;
	onKeyDown?: (args: KeyboardEvent<HTMLInputElement>) => void;
	ref?: Ref<HTMLInputElement>;
};

export const Checkbox = (props: CheckboxProps): ReactElement => {
	const {
		value,
		isDisabled,
		text,
		title,
		ellipseText = true,
		isCentered = false,
		onChange,
		trackingId,
		icon,
		color,
		hasBusyIndicator = false,
		shouldBubbleClick,
		tabIndex,
		onKeyDown,
		ref
	} = props;

	const { id } = useContext(UniqueInputIdContext);

	const resolvedText = useMemo(
		(): string | undefined => resolveFormattedTextString(text),
		[text]
	);

	const colorStyle: string | undefined = color
		? checkboxColorToStylesMap[color]
		: undefined;

	const changeHandler = (args: ChangeEvent<HTMLInputElement>): void => {
		if (shouldBubbleClick === false) {
			args.stopPropagation();
		}

		onChange(args.target.checked);
	};

	const rootClickHandler = useCallback(
		(event: MouseEvent): void => {
			if (shouldBubbleClick === false) {
				event.stopPropagation();
			}
		},
		[shouldBubbleClick]
	);

	return (
		<label
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_disabled]: isDisabled,
					[styles.root_centered]: isCentered,
				}
			)}
			title={title ?? resolvedText}
			onClick={rootClickHandler}
			onDoubleClick={rootClickHandler}
		>
			<input
				{...setTrackingId("input")}
				onKeyDown={onKeyDown}
				ref={ref}
				type="checkbox"
				id={id}
				checked={value}
				disabled={isDisabled}
				onChange={changeHandler}
				tabIndex={tabIndex}
			/>
			<span
				aria-hidden
				className={classNames(
					styles.glyph,
					colorStyle,
					{
						[styles.glyph_colored]: isNotNil(color)
					}
				)}
			>
				{value && <Icon value="check" size="medium" />}
			</span>
			{resolvedText && (
				<span
					className={classNames(
						styles.text,
						{
							[styles.text_ellipsed]: ellipseText,
							[styles.text_primary]: value && color === "primary",
						}
					)}
				>
					{resolvedText}
				</span>
			)}
			{(icon || hasBusyIndicator) && (
				<div className={styles.iconAndSuffixWrapper}>
					{!hasBusyIndicator && icon && (
						<span
							className={classNames(
								{ [styles.icon_svg]: isSvgIcon(icon) }
							)}
						>
							<Icon
								value={isDisabled
									? unsetIconColor(icon)
									: icon
								}
							/>
						</span>
					)}
					{hasBusyIndicator && (
						<Spinner size="small"/>
					)}
				</div>
			)}
		</label>
	);
};
Checkbox.displayName = "Checkbox";

export type CheckboxReadOnlyProps = {
	value: boolean;
	trackingId?: string;
	text?: string | FormattedTextString;
	title?: string;
	ellipseText?: boolean;
};

export const CheckboxReadOnly = (props: CheckboxReadOnlyProps): ReactElement => {
	const {
		value,
		trackingId,
		text,
		ellipseText = true
	} = props;

	const resolvedText = useMemo(
		(): string | undefined => resolveFormattedTextString(text),
		[text]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			<span
				className={styles.glyph}
			>
				{value && <Icon value="check" size="medium" />}
			</span>
			{resolvedText && (
				<span
					className={classNames(
						styles.text,
						{
							[styles.text_ellipsed]: ellipseText
						}
					)}
				>
					{resolvedText}
				</span>
			)}
		</div>
	);
};
CheckboxReadOnly.displayName = "CheckboxReadOnly";

const checkboxColorToStylesMap: { [key in CheckboxColor]: string } = {
	success: styles.glyph_success,
	info: styles.glyph_info,
	danger: styles.glyph_error,
	warning: styles.glyph_warning,
	"calendar-1": styles.glyph_calendar1,
	"calendar-2": styles.glyph_calendar2,
	"calendar-3": styles.glyph_calendar3,
	"calendar-4": styles.glyph_calendar4,
	"calendar-5": styles.glyph_calendar5,
	"calendar-6": styles.glyph_calendar6,
	"calendar-7": styles.glyph_calendar7,
	"calendar-8": styles.glyph_calendar8,
	"calendar-9": styles.glyph_calendar9,
	"calendar-10": styles.glyph_calendar10,
	"calendar-11": styles.glyph_calendar11,
	"calendar-12": styles.glyph_calendar12,
	"calendar-13": styles.glyph_calendar13,
	"calendar-14": styles.glyph_calendar14,
	"calendar-15": styles.glyph_calendar15,
	"calendar-16": styles.glyph_calendar16,
	"primary": styles.glyph_primary
};
